import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { UserData } from '../services/userManagement';

interface UserTableProps {
  users: UserData[];
  loading: boolean;
  onEdit: (user: UserData) => void;
  onDelete: (username: string) => void;
}

const getRoleBadgeColor = (role: string): 'error' | 'primary' | 'default' => {
  switch (role) {
    case 'admin':
      return 'error';
    case 'analyst':
      return 'primary';
    default:
      return 'default';
  }
};

const UserTable: React.FC<UserTableProps> = ({ users, loading, onEdit, onDelete }) => {
  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  if (users.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No users found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'primary.light' }}>
            <TableCell><strong>Username</strong></TableCell>
            <TableCell><strong>Full Name</strong></TableCell>
            <TableCell><strong>Email</strong></TableCell>
            <TableCell><strong>Role</strong></TableCell>
            <TableCell><strong>Status</strong></TableCell>
            <TableCell><strong>Created</strong></TableCell>
            <TableCell align="center"><strong>Actions</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow
              key={user.uid}
              sx={{
                '&:hover': { bgcolor: 'action.hover' },
                opacity: user.disabled ? 0.6 : 1,
              }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {user.username}
                </Typography>
              </TableCell>
              <TableCell>{user.full_name || '-'}</TableCell>
              <TableCell>{user.email || '-'}</TableCell>
              <TableCell>
                <Chip
                  label={user.role.toUpperCase()}
                  size="small"
                  color={getRoleBadgeColor(user.role)}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={user.disabled ? 'Disabled' : 'Active'}
                  size="small"
                  color={user.disabled ? 'default' : 'success'}
                  variant={user.disabled ? 'outlined' : 'filled'}
                />
              </TableCell>
              <TableCell>
                {user.created_at
                  ? new Date(user.created_at).toLocaleDateString()
                  : '-'
                }
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Edit user">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onEdit(user)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete user">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(user.username)}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default UserTable;
